import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import * as _ from 'lodash-es';
import {IAnswerItem, IGenericObj, IGroupType, ILimitItem, IObjString, IPageDataBoxType, IhightLightInfo, IlistItem, IGetAnswerList, IParamsType, IQuestionResult, IResult} from '@/utils/ctype';
import {UNANSWERED, UNANSWEREDWRAP} from '@/utils/contant';
import { getTotalAudioDuration } from '@/utils/getAudioTotalDuration';
import { getListeningContent, getAnswerList, saveAnswerList, submitAnswer, getResult } from '@/api/listen';
import type { ExamRequestOptions } from '@/api/type';
import {renderHightLightPage, commonReducers} from './common';

export interface IListeningTopicListState {
  showNotePanel: boolean;
  pageDataAll: IPageDataBoxType[];
  pageDataCache: Array<IPageDataBoxType| undefined>;
  pageData: IPageDataBoxType | null;
  recordId: number;
  limits: ILimitItem[];
  answerAll: Array<IAnswerItem| null | undefined>;
  answerMap: IGenericObj<string[]>;
  result: IQuestionResult[];
  resultMap: IGenericObj<string>;
  displayAnswerMap: IGenericObj<string>;
  currentFocus: number;
  pageDataIndex: number;
  loadAnswered: boolean;
  highLightInfo: IhightLightInfo;
  surplus_time: number;
  is_confirm: number;
  audioSrcArr: string[];
  audioIndex: number;
  audioPlayTime: number;
  audioStartPlayTime: number;
  isSubject: boolean;
  showDragField: boolean;
  playStatus: 'none'| 'play'| 'pause';
  playIndex: number;
  recordAudioProgressArr: Array<{duration: number; currentTime: number;}>;
  totalDuration: number;
  draftSyncedAt: number;
  draftSavePending: boolean;
}

const initialState: IListeningTopicListState = {
  showNotePanel: false,
  pageDataCache: [],
  pageDataAll: [],
  pageData: null,
  limits: [],
  answerAll: [],
  answerMap: {},
  displayAnswerMap: {},
  result: [],
  resultMap: {},
  currentFocus: 1,
  recordId: 1,
  pageDataIndex: 0,
  loadAnswered: false,
  highLightInfo: {},
  surplus_time: 0,
  is_confirm: 0,
  audioSrcArr: [],
  audioIndex: 0,
  audioPlayTime: 0,
  audioStartPlayTime: 0,
  isSubject: true,
  showDragField: false,
  playStatus: 'none',
  playIndex: -1,
  recordAudioProgressArr: [],
  totalDuration: 0,
  draftSyncedAt: 0,
  draftSavePending: false,
};

export const getAnswerListAsync = createAsyncThunk<IGetAnswerList| undefined, {record_id: number;}>('listeningList/getAnswerList', async (params, {rejectWithValue}) => {
  try {
    const res = await getAnswerList(params);
    if (res.code !== 200) {
      return rejectWithValue(null);
    }

    return res.data;
  } catch (err) {
    return rejectWithValue(null);
  }
});

export const saveAnswerListAsync = createAsyncThunk<{id: number;} | undefined, ExamRequestOptions | void>(
  'listeningList/saveAnswerListAsync',
  async (params, {rejectWithValue, getState, signal}) => {
    try {
      const state:any = getState();
      const recordId = Number(state.listening.recordId);
      if (params?.recordId !== undefined && Number(params.recordId) !== recordId) {
        return rejectWithValue(null);
      }
      const {audioIndex, audioPlayTime} = state.listening;
      const res = await saveAnswerList({
        record_id: recordId,
        answer: state.listening.answerAll.filter((item:any) => item),
        mark: state.listening.highLightInfo ? JSON.stringify(state.listening.highLightInfo) : JSON.stringify({}),
        surplus_time: state.listening.surplus_time,
        is_confirm: state.listening.is_confirm,
        audio_location: JSON.stringify({audioIndex, audioPlayTime: audioPlayTime.toFixed(3)})
      }, {signal: params?.signal ?? signal});
      if (res.code !== 200) {
        return rejectWithValue(null);
      }
      return res.data;
    } catch (err) {
      console.error('err');
      return rejectWithValue(null);
    }
  }, {
    condition: (_params, {getState}) => !(getState() as any).listening.draftSavePending,
  });

export const getListeningContentAsync = createAsyncThunk<{ list: IPageDataBoxType[]; totalDuration: number; }| undefined, {record_id: number;}>('listeningList/getPageData', async (params, {rejectWithValue, getState}) => {
  try {
    const res = await getListeningContent(params);
    if (res.code !== 200) {
      return rejectWithValue(null);
    }
    const list = res.data.list.map((item) => item.question.file_url);
    const totalDuration = await getTotalAudioDuration(list);
    // 实体对象会影响选区定位，这里做一下处理
    const newList = JSON.parse(JSON.stringify(res.data.list).replaceAll('&nbsp;', ' '));
    return {
      // list: res.data.list,
      list: newList,
      totalDuration: totalDuration + 2 * 60,
    };
  } catch (err) {
    return rejectWithValue(null);
  }
});

export const getListeningFeedbackAsync = createAsyncThunk<IResult | undefined, IParamsType>('listeningList/getListeningResult', async (params, { rejectWithValue }) => {
  try {
    const res = await getResult(params);
    if (res.code !== 200) {
      return rejectWithValue(null);
    }
    return res.data;
  } catch (err) {
    return rejectWithValue(null);
  }
});

export const submitAnswerAsync = createAsyncThunk<unknown, ExamRequestOptions | void>('listeningList/submitAnswer', async (params, {rejectWithValue, getState, signal}) => {
  try {
    const recordId = Number((getState() as any).listening.recordId);
    if (params?.recordId !== undefined && Number(params.recordId) !== recordId) {
      return rejectWithValue(null);
    }
    const res = await submitAnswer({record_id: recordId}, {signal: params?.signal ?? signal});
    if (res.code !== 200) {
      return rejectWithValue(null);
    }
    return res.data;
  } catch (err) {
    return rejectWithValue(null);
  }
});

export const listeningSlice = createSlice({
  name: 'listening',
  initialState,
  reducers: {
    ...commonReducers,
    saveAnswer: (state, {payload}: {payload: {index: number; value: IAnswerItem | null | undefined;} | {index: number[]; value: Array<IAnswerItem | null | undefined>;};}) => {
      const {index, value} = payload;
      const answerAll = state.answerAll.slice();
      const answerMap = Object.assign(state.answerMap);
      if (Array.isArray(index) && Array.isArray(value)) {
        index.forEach((indexItem, index2) => {
          answerAll[indexItem - 1] = value[index2];
          if (value[index2]) {
            const {group_id, id, type, answer} = value[index2] as IAnswerItem;
            answerMap[`${group_id}_${type}_${id|| ''}`] = answer;
          }
        });
        state.currentFocus = index[0];
      } else if (!Array.isArray(value)&& !Array.isArray(index)) {
        answerAll[index - 1] = value;
        if (value) {
          const {group_id, id, type, answer} = value;
          answerMap[`${group_id}_${type}_${id|| ''}`] = answer;
        }
        
        state.currentFocus = index;
      }
      state.answerAll = answerAll;
      state.answerMap = answerMap;
      console.log('answerAll', answerAll);
    },
    setPlayIndex: (state, {payload}) => {
      state.audioIndex = payload;
    },
    setIsSubject(state, {payload}) {
      state.isSubject = payload;
    },
    setShowDragFiled(state, {payload}) {
      state.showDragField = payload;
    },
    setPlayInfo(state, {payload}) {
      const {playStatus, playIndex} = payload;
      state.playStatus = playStatus;
      state.playIndex = playIndex;
    },
    setPlayStatus(state, {payload}) {
      state.playStatus = payload;
    },
    setAudioPlayTime(state, {payload}) {
      state.audioPlayTime = payload;
    },
    setRecordAudioProgress(state, { payload }) {
      const pageDataIndex = state.pageDataIndex;
      state.recordAudioProgressArr[pageDataIndex] = payload;
    }
  },
  extraReducers: (builder) => {

    const getIdToContentMap = (data: IGroupType) => {
      const {type, collect_option} = data;
      switch (type) {
        case 1:
        case 2: 
        case 3: 
        case 4:
        case 5: 
        case 6: 
        case 7: 
          return null;
        case 8:
          return collect_option.list.reduce((memo: IObjString, item) => {
            memo[item.id] = item.title || item.content;
            return memo;
          }, {});
      }
    };
    const getAnswerListHandle = (
      item: IlistItem,
      answer: string[],
      type: number,
      arr: Array<string | string[]| undefined | null>,
      idToContentMap: IObjString|undefined|null,
    ) => {
      const { number, option} = item;
      switch (type) {
        case 1: 
          arr[number - 1] = option.find((item) => String(item.id) === answer?.[0])?.title || UNANSWEREDWRAP;
          break;
        case 8:  
          arr[number - 1] = (idToContentMap && idToContentMap[answer?.[0]]) || UNANSWERED;
          break; 
        case 2:
        case 7:
          arr[number - 1] = answer?.[0] || UNANSWEREDWRAP;
          break;
        case 3: 
        case 4:
        case 5: 
          if (item.is_multiple === 1) {
            arr[number - 1] = answer && answer.length > 0 ? answer : UNANSWERED;
            break;
          } else {
            arr[number - 1] = answer?.[0] || UNANSWERED;
            break;

          }
        case 6: 
          arr[number - 1] = answer?.[0] || UNANSWERED;
          break;
      }
    };
    const getFeedback = (s: number) => {
      if (s === 1) {
        return 'success';
      }
      if (s === 2) {
        return 'fail';
      }
      return 'normal';
    };
    const dealAnwer = (state: IListeningTopicListState) => {
      const answerMap = state.answerMap;
      if (state.pageDataAll && state.loadAnswered) {
        const result: Array<IAnswerItem | null | undefined> = [];
        const answerList: string[] = [];
        state.pageDataAll.map((item1 => {
          item1.question.question.forEach((item2, index2: number) => {
            const {group_id, type} = item2;
            const isSingle = type !== 2;
            const idToContentMap = getIdToContentMap(item2);
            item2.list.forEach((item3, index3: number) => {
              const {id} = item3;
              const key = `${group_id}_${type}_${ isSingle? id : ''}`;
              const answer = answerMap[key]; 
              getAnswerListHandle(item3, answer, type, answerList, idToContentMap);
              if (isSingle) {
                result.push(answer ? {group_id, type, id, answer } : undefined);
              } else {
                if (answer && answer.length > index3) {
                  result.push( index3 === 0 ? {group_id, type, answer } : null) ;
                } else {
                  result.push(undefined);
                }
              }
            });
          });
        }));
        
        state.pageDataAll.forEach(item1 => {item1.answerAllText = answerList;});
        state.pageDataCache.forEach(item1 => {item1 && (item1.answerAllText = answerList);});
        
        console.log('获取接口处理结果', result, answerList);
        state.answerAll = result;
      }
      // 显示高亮
      const curPageData = state.pageDataAll[state.pageDataIndex];
      const highLightObj = state.highLightInfo[state.pageDataIndex];
      if (curPageData && highLightObj) {
        const cloneCurPageData = _.cloneDeep(curPageData);
        renderHightLightPage(highLightObj, cloneCurPageData);
        const pageDataCache = [...state.pageDataCache];
        pageDataCache[state.pageDataIndex] = cloneCurPageData;
        state.pageDataCache = pageDataCache;
      }
    };
    builder.addCase(getListeningContentAsync.fulfilled, (state, action) => {
      const audioSrcArr: string[] = [];
      if (action.payload) {
        // 预处理
        action.payload.list.forEach((item1) => {
          audioSrcArr.push(item1?.question.file_url);
          item1.question.question.forEach((item2)=> {
            
            if (item2.type === 2) {
              const l = item2.list.map((item3) => item3.number);
              item2.numbers = `${Math.min(...l)}-${Math.max(...l)}`;
              // item2.numbers = item2.list.map((item3) => item3.number).join('-');
            }
            item2.list.forEach((item3) => {
              item3.display_answerHtml = '(' + item3.display_answer + ')'; 
            });
          });
        });
        state.audioSrcArr = audioSrcArr;
        state.pageDataAll = action.payload.list;
        state.pageDataIndex = 0;
        if (state.pageDataAll) {
          state.pageDataCache = [state.pageDataAll[state.pageDataIndex]];
          state.pageData = state.pageDataAll[state.pageDataIndex];
          let len = 0;
          state.limits = state.pageDataAll.map((item => {
            const itemQuestion = item.question;
            len += itemQuestion.question_number.length;
            return {
              min: itemQuestion.question_number[0],
              max: itemQuestion.question_number[itemQuestion.question_number.length - 1]
            };
          }));
          state.answerAll = new Array(len);
        }
        state.totalDuration = action.payload.totalDuration || 30 * 60;

        dealAnwer(state);
      }
      
    }).addCase(getAnswerListAsync.fulfilled, (state, action) => {
      const data = action.payload;

      if ( data) {
        state.loadAnswered = true;
        state.draftSyncedAt = Date.now();

        const {answer, surplus_time, mark, is_confirm, audio_location} = data;
        state.surplus_time = surplus_time || Math.floor(state.totalDuration);
        state.highLightInfo = mark ? JSON.parse(mark) : {};

        console.log('高亮数据', state.highLightInfo);
        state.is_confirm = is_confirm;

        if (audio_location) {
          try {
            const audioLocation = JSON.parse(audio_location);
            const {audioIndex, audioPlayTime} = audioLocation;
            state.audioIndex = audioIndex;
            state.audioStartPlayTime = Number(audioPlayTime);
            state.audioPlayTime = Number(audioPlayTime);
          } catch (e) {
            console.error(e);
          }
          
        }

        const answerMap: IGenericObj<string[]> = {};
        if (answer) {
          answer.map((item) => {
            const {group_id, id, type, answer} = item;
            answerMap[`${group_id}_${type}_${id ? id : ''}`] = answer;
          });
          state.answerMap = answerMap;
        }
        dealAnwer(state);
      }

      console.log('请求草稿接口', state.answerMap);
    }).addCase(getListeningFeedbackAsync.fulfilled, (state, action) => {
      if (action.payload?.list) {
        state.result = action.payload?.list;
        const resultMap: IGenericObj<string> = {};
        const displayAnswerMap: IGenericObj<string> = {};
        action.payload.list.forEach((item) => {
          const { question_id, result} = item;
          resultMap[question_id] = getFeedback(result);
          console.log('resultMap', item.type, item.is_multiple, item);
          if (item.is_multiple === 1 && item.multiple_answer && item.multiple_answer.length > 0) {
            displayAnswerMap[question_id] = '(' + item.multiple_answer[0].join(' ; ') + ')'; 
          }
        });
        state.resultMap = resultMap;
        state.displayAnswerMap = displayAnswerMap;
        dealAnwer(state);
      }
    }).addCase(saveAnswerListAsync.pending, (state) => {
      state.draftSavePending = true;
    }).addCase(saveAnswerListAsync.fulfilled, (state) => {
      state.draftSavePending = false;
      state.draftSyncedAt = Date.now();
    }).addCase(saveAnswerListAsync.rejected, (state) => {
      state.draftSavePending = false;
    }).addCase(submitAnswerAsync.fulfilled, () => {});
  },
});

export const {toggleShowNotePanel, setPanelDataByIndex, setFocusIndex, saveAnswer,
  saveHighlightData, showDialog, hideDialog, updateTimeState, updateRecordId, clearHightLightInfo, updatePanelInfo,
  deleteNote, setPlayIndex, setIsSubject, setShowDragFiled, setPlayInfo, setPlayStatus, setAudioPlayTime, setRecordAudioProgress} = listeningSlice.actions;
